import React from 'react'

import * as c from '../../../common'
import * as r from '../../../react-utils'

// import { useLoadScript } from '@react-google-maps/api';

import Pad from '../Pad'
import PageReport from '../PageReport'

import RCInput from './RCInput'
import RoofClaimingContractHeader from './RoofClaimingContractHeader'
import SignatureButton from './SignatureButton'
import { RoofClaimContext } from './RoofClaimContext'
import styles from './RoofClaimingContractMainPage.module.css'

export interface RoofClaimingContractMainPageProps {
  company: string
  customer: c.Customer
  customerID: string
  noPDF?: boolean
  onOpenRoofClaimClientSigning: c.VoidFn
  onOpenRoofClaimRepSigning: c.VoidFn
}

export default React.memo<RoofClaimingContractMainPageProps>(
  function RoofClaimingContractMainPage({
    company,
    customerID,
    noPDF,
    onOpenRoofClaimClientSigning,
    onOpenRoofClaimRepSigning,
  }) {
    const { contractCompanyFull, shouldOfferCompanySelector } =
      r.useContractCompany(customerID)

    const { locked, readOnly, isPDF } = React.useContext(RoofClaimContext)
    const companyName = shouldOfferCompanySelector
      ? contractCompanyFull.longName
      : c.RoofClaimingContractorToLabel.ecohome

    return (
      <PageReport
        autoHeight={true}
        company={company}
        companyBorder={false}
        noPDF={noPDF}
        title=""
      >
        <div className={styles['container']}>
          <Pad amt={30} />
          <RoofClaimingContractHeader />
          <Pad amt={30} />
          <fieldset
            disabled={locked || readOnly || isPDF}
            style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
          >
            <div className={styles['inputs-horizontal']}>
              <RCInput
                customerID={customerID}
                field="roof_claiming_date"
                height="lg"
                inputType="date"
                type="input"
              />
              <Pad amt={20} row />
              <RCInput
                customerID={customerID}
                field="roof_claiming_homeowner"
                height="lg"
                inputType="text"
                type="input"
              />
            </div>
            <Pad amt={20} />
            <div className={styles['inputs-horizontal']}>
              <RCInput
                customerID={customerID}
                field="roof_claiming_phone"
                height="lg"
                inputType="tel"
                type="input"
              />
              <Pad amt={20} row />
              <RCInput
                customerID={customerID}
                field="roof_claiming_phone_alt"
                height="lg"
                inputType="tel"
                type="input"
              />
              <Pad amt={20} row />
              <RCInput
                customerID={customerID}
                field="roof_claiming_email"
                height="lg"
                inputType="text"
                type="input"
              />
            </div>
            <Pad amt={20} />
            <div className={styles['inputs-horizontal']}>
              <RCInput
                customerID={customerID}
                // defaultValue={address}
                field="roof_claiming_address"
                height="lg"
                inputType="text"
                type="input"
              />
              <Pad amt={20} row />
              <RCInput
                customerID={customerID}
                // defaultValue={city}
                field="roof_claiming_city"
                height="lg"
                inputType="text"
                type="input"
              />
              <Pad amt={20} row />
              <RCInput
                customerID={customerID}
                // defaultValue={zipCode}
                field="roof_claiming_zip"
                height="lg"
                inputType="text"
                type="input"
              />
            </div>

            <Pad amt={30} />
            <div className={styles['input-label-horizontal']}>
              <span>
                {companyName}, proposes to furnish all materials and labor in
                accordance with the specifications below for the sum of:{' '}
                <strong>RCV</strong> With your approval, {companyName}, will
                perform the repairs or replacements specified by your insurance
                company for <strong>INSURANCE APPROVED PROCEEDS ONLY </strong>{' '}
                with no additional cost to the homeowner except for the
                deductible. Any upgrades or additional work authorized by you
                that is not a part of your insurance claim is considered an
                additional expense above and beyond the insurance proceeds. All
                supplemental claims billed by {companyName} on behalf of the
                homeowner which are approved by the insurance company for
                additional work or increase in cost, are in addition to this
                agreement and are part of the agreement contained herein.
              </span>
            </div>

            <Pad amt={30} />
            <div className={styles['inputs-horizontal']}>
              <RCInput
                customerID={customerID}
                field="roof_claiming_deductible"
                height="lg"
                inputType="text"
                type="input"
              />
              <Pad amt={20} row />
              <RCInput
                customerID={customerID}
                field="roof_claiming_shingle_manufacturer"
                height="lg"
                inputType="text"
                type="input"
              />
            </div>
            <Pad amt={20} />
            <div className={styles['inputs-horizontal']}>
              <RCInput
                customerID={customerID}
                field="roof_claiming_style"
                height="lg"
                inputType="text"
                type="input"
              />
              <Pad amt={20} row />
              <RCInput
                customerID={customerID}
                field="roof_claiming_color"
                height="lg"
                inputType="text"
                type="input"
              />
              <Pad amt={20} row />
              <RCInput
                customerID={customerID}
                field="roof_claiming_drip_color"
                height="lg"
                inputType="text"
                type="input"
              />
            </div>
            <Pad amt={30} />
            {/* SCOPE NOTES */}

            <div className={styles['scope-notes']}>
              <h4>Scope Notes</h4>
              <Pad amt={20} />
              <div className={styles['inputs-horizontal']}>
                <RCInput
                  customerID={customerID}
                  field="roof_claiming_age"
                  height="lg"
                  inputType="text"
                  type="input"
                />
                <Pad amt={20} row />
                <RCInput
                  customerID={customerID}
                  field="roof_claiming_layers"
                  height="lg"
                  inputType="text"
                  type="input"
                />
                <Pad amt={20} row />
                <RCInput
                  customerID={customerID}
                  field="roof_claiming_predominate_pitch"
                  height="lg"
                  inputType="text"
                  type="input"
                />
              </div>
              <Pad amt={30} />
              <div className={styles['checkbox-container']}>
                <div className={styles['checkbox-group']}>
                  {roofClaimingCheckboxKeys.map((key) => (
                    <RCInput
                      customerID={customerID}
                      field={key as keyof c.Customer}
                      key={key}
                      type="checkbox"
                    />
                  ))}
                  <Pad amt={5} />
                </div>
                <div className={styles['input-groups']}>
                  <div className={styles['inputs-vertical']}>
                    <h4 className={styles['attachments-title']}>Attachments</h4>
                    <Pad amt={10} />
                    {roofClaimingInputsAttachmentKeys.map((key, i) => (
                      <React.Fragment key={key}>
                        <RCInput
                          customerID={customerID}
                          field={key as keyof c.Customer}
                          height="sm"
                          inputType="text"
                          type="input"
                        />
                        {i + 1 !== roofClaimingInputsAttachmentKeys.length ? (
                          <Pad amt={5} />
                        ) : null}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <Pad amt={30} />
            {/* NOTES */}
            <RCInput
              customerID={customerID}
              field="roof_claiming_notes"
              type="textarea"
            />
            <Pad amt={30} />
            <div className={styles['inputs-horizontal']}>
              <RCInput
                customerID={customerID}
                field="roof_claiming_date_loss"
                height="lg"
                inputType="date"
                type="input"
              />
              <Pad amt={20} row />
              <RCInput
                customerID={customerID}
                field="roof_claiming_insurance_company"
                height="lg"
                inputType="text"
                type="input"
              />
            </div>
            <Pad amt={20} />
            <div className={styles['inputs-horizontal']}>
              <RCInput
                customerID={customerID}
                field="roof_claiming_claim"
                height="lg"
                inputType="text"
                type="input"
              />
            </div>
            <Pad amt={20} />
            <p className={styles['text']}>
              Supplemental claims consist for submitting to the Insurance
              Company for payment for any items required to meet code or to
              properly perform the work, for damaged items missing in the scope
              of insurance estimate, or for pricing that is below fair and
              reasonable market conditions
            </p>
            <Pad amt={10} />
            <p className={styles['text']}>
              <strong>
                Homeowner is under no obligation to {companyName} unless their
                Insurance Company Approves the Claim.
              </strong>
            </p>
            <Pad amt={30} />
          </fieldset>
          <div className={styles['signatures']}>
            <SignatureButton
              customerID={customerID}
              field="roof_claiming_client_sign"
              handleOpen={onOpenRoofClaimClientSigning}
            />
            <SignatureButton
              customerID={customerID}
              field="roof_claiming_company_rep_sign"
              handleOpen={onOpenRoofClaimRepSigning}
            />
          </div>
        </div>
      </PageReport>
    )
  },
)

const roofClaimingCheckboxKeys = [
  'roof_claiming_tear_off_layers_shingles',
  'roof_claiming_install_felt',
  'roof_claiming_close_valleys',
  'roof_claiming_ridges_color_coordinated',
  'roof_claiming_install_new_flashings',
  'roof_claiming_replace_ventilation',
  'roof_claiming_install_nails',
  'roof_claiming_clean_job_waste_gutters',
  'roof_claiming_two_year_workmanship_warranty',
  'roof_claiming_magnetic_sweep_property',
]

const roofClaimingInputsAttachmentKeys = [
  'roof_claiming_pipe_jack',
  'roof_claiming_chimney_flashing',
  'roof_claiming_chimney_cap',
  'roof_claiming_digital_satellite',
  'roof_claiming_gas_cap',
  'roof_claiming_turtle_vent',
  'roof_claiming_ridge_vent',
  'roof_claiming_turbine_vent',
  'roof_claiming_power_attic_vent',
  'roof_claiming_other_one',
  'roof_claiming_other_two',
  'roof_claiming_other_three',
]
